import { useMemo } from 'react';

/** Valid anime expressions (superset — other modes map into these) */
type AnimeExpression = 'neutral' | 'happy' | 'suspicious' | 'surprised' | 'sad' | 'wink' | 'focused' | 'love' | 'idle';

/** Mood tag regex — matches [saxa:expression] at end of text */
const MOOD_TAG_RE = /\[saxa:(\w+)\]\s*$/;
/** Partial tag that might appear mid-stream (e.g. "[saxa:hap") */
const PARTIAL_TAG_RE = /\[saxa:\w*$/;

/** Strip mood tags (complete + partial) from display text */
export function stripMoodTag(text: string): string {
  return text.replace(MOOD_TAG_RE, '').replace(PARTIAL_TAG_RE, '').trimEnd();
}

/** Keyword → expression fallback heuristics */
const KEYWORD_MAP: [RegExp, AnimeExpression][] = [
  [/\b(sorry|apologize|unfortunately|can't help)\b/i, 'sad'],
  [/\b(great|awesome|glad|love it|fantastic|excited)\b/i, 'happy'],
  [/\b(hmm|interesting|curious|wonder)\b/i, 'suspicious'],
  [/\b(wow|amazing|incredible|no way)\b/i, 'surprised'],
  [/\b(haha|lol|😉|wink)\b/i, 'wink'],
  [/\b(sure|let me|here's|check|looking)\b/i, 'focused'],
  [/\b(❤|love|heart)\b/i, 'love'],
];

function inferFromKeywords(text: string): AnimeExpression {
  for (const [re, expr] of KEYWORD_MAP) {
    if (re.test(text)) return expr;
  }
  return 'neutral';
}

/** Mode-specific expression mapping */
const PLAIN_MAP: Record<string, string> = {
  neutral: 'neutral', happy: 'happy', suspicious: 'thinking', surprised: 'surprised',
  sad: 'sad', wink: 'wink', focused: 'thinking', love: 'happy', idle: 'idle',
};

const MANGA_MAP: Record<string, string> = {
  neutral: 'neutral', happy: 'curious', suspicious: 'ponder', surprised: 'shock',
  sad: 'ponder', wink: 'curious', focused: 'fierce', love: 'curious', idle: 'idle',
};

const RUNE_MAP: Record<string, string> = {
  neutral: 'idle', happy: 'responding', suspicious: 'thinking', surprised: 'processing',
  sad: 'error', wink: 'greeting', focused: 'thinking', love: 'greeting', idle: 'idle',
};

const AI_MAP: Record<string, string> = {
  neutral: 'idle', happy: 'responding', suspicious: 'analyzing', surprised: 'processing',
  sad: 'idle', wink: 'responding', focused: 'thinking', love: 'creating', idle: 'idle',
};

/** Map an anime-canonical expression to the appropriate mode expression name */
export function mapExpression(expression: string, mode: string): string {
  if (mode === 'anime') return expression;
  if (mode === 'plain') return PLAIN_MAP[expression] || 'neutral';
  if (mode === 'manga') return MANGA_MAP[expression] || 'neutral';
  if (mode === 'runes') return RUNE_MAP[expression] || 'idle';
  if (mode === 'ai') return AI_MAP[expression] || 'idle';
  return expression;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
}

/**
 * Derives Saxa's current expression from chat messages and loading state.
 * Returns the canonical (anime-namespace) expression name — call mapExpression()
 * for other modes.
 */
export function useSaxaExpression(
  messages: Message[],
  isLoading: boolean,
): string {
  return useMemo(() => {
    // While loading with no content yet → focused (typing)
    const lastMsg = [...messages].reverse().find((m) => m.role === 'assistant');
    if (isLoading && (!lastMsg || !lastMsg.content)) return 'focused';

    if (!lastMsg || !lastMsg.content) return 'neutral';

    const text = lastMsg.content;

    // Try mood tag first
    const tagMatch = text.match(MOOD_TAG_RE);
    if (tagMatch) {
      const expr = tagMatch[1] as string;
      // Validate it's a known expression
      const valid: AnimeExpression[] = ['neutral', 'happy', 'suspicious', 'surprised', 'sad', 'wink', 'focused', 'love', 'idle'];
      if (valid.includes(expr as AnimeExpression)) return expr;
    }

    // Fallback to keyword heuristics
    return inferFromKeywords(text);
  }, [messages, isLoading]);
}
